import { type Announcement, can } from '@af/shared';
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { NewChat } from '@/components/chat/NewChat';
import { CompanyOverview } from '@/components/company/CompanyOverview';
import { RightPanel } from '@/components/dashboard/RightPanel';
import { HelpLauncher, HelpPanel } from '@/components/help/HelpLauncher';
import { ChannelView } from '@/components/talk/ChannelView';
import { TalkSidebar } from '@/components/talk/TalkSidebar';
import { Spinner } from '@/components/ui/button';
import { IconAlert, IconLock, IconStop } from '@/components/ui/icons';
import { Empty } from '@/components/ui/misc';
import { paneFromPath, useWorkspace, WorkspaceProvider } from '@/components/workspace/store';
import { Workspace } from '@/components/workspace/Workspace';
import { authClient } from '@/lib/auth/better-auth';
import { useHotkeys } from '@/lib/hotkeys';
import { contextKindFor } from '@/lib/page-context';
import { OPEN_PALETTE_EVENT } from '@/lib/palette';
import { RoleProvider } from '@/lib/permissions';
import { useAgents, useMe, useOrgEvents, useOrgStatus, usePriceMissing, useUnreadCount } from '@/lib/queries';
import { EXTRA_GO } from '@/lib/shortcuts';
import { useIsPhone } from '@/lib/use-media';
import { cn, safeStorage } from '@/lib/utils';
import { BillingPage } from '@/routes/billing';
import { SecureAccountGate } from '@/routes/secure-account';
import { SecuritySection } from '@/routes/settings/security';
import { AnnouncementBanner } from './AnnouncementBanner';
import { BottomNav } from './BottomNav';
import { CommandPalette } from './CommandPalette';
import { NAV } from './nav';
import { ShortcutsOverlay } from './ShortcutsOverlay';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

const isDesktop = () => typeof window !== 'undefined' && window.matchMedia?.('(min-width: 1024px)').matches;
// The inspector sits inline from 1536px (next to the sidebar, chat list and chats); below that it overlays.
const isWide = () => typeof window !== 'undefined' && window.matchMedia?.('(min-width: 1536px)').matches;

function Frame() {
  const { t } = useTranslation();
  const me = useMe();
  const [dismissedAnnouncement, setDismissedAnnouncement] = useState(readDismissed);
  const dismissAnnouncement = () => {
    const id = me.data?.announcement?.id ?? null;
    setDismissedAnnouncement(id);
    try {
      if (id) globalThis.localStorage?.setItem(DISMISSED_KEY, id);
    } catch {
      // Private mode: dismissed for this page view only.
    }
  };
  const role = me.data?.role ?? null;
  const hasOrg = !!me.data?.organization;
  const status = useOrgStatus();
  const unread = useUnreadCount();
  useOrgEvents(hasOrg);
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const routePane = paneFromPath(pathname);
  const ws = useWorkspace();
  const phone = useIsPhone();
  const [drawer, setDrawer] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(() => safeStorage.get<boolean>('af:sidebar') ?? true);
  // Explicit choice (persisted on wide screens); null = default: open only when there are agents.
  const [panelPref, setPanelPref] = useState<boolean | null>(() =>
    isWide() ? safeStorage.get<boolean>('af:panel') : false,
  );
  const agents = useAgents();
  const hasAgents = (agents.data?.length ?? 0) > 0;
  const panelOpen = panelPref ?? hasAgents;
  const [companyAgent, setCompanyAgent] = useState<{ id: string; selection: number } | null>(null);
  const [palette, setPalette] = useState(false);
  const [paletteInit, setPaletteInit] = useState('');
  const [help, setHelp] = useState(false);
  const [helpChatOpen, setHelpChatOpen] = useState(false);
  const closeHelpChat = () => {
    setHelpChatOpen(false);
    document.getElementById('help-launcher')?.focus();
  };
  const showHelpChat = hasOrg && helpChatOpen && !pathname.startsWith('/help/chat');
  const priceMissing = usePriceMissing(can(role, 'provider:read'));

  // From the location itself: during a navigation the matched route (and its search) can still be the old one.
  const search = useRouterState({ select: (s) => s.location.search }) as {
    new?: boolean;
    agent?: string;
    thread?: string;
  };
  // A channel or agent DM (`/chat/c/:id`) is not a workspace pane: the chat sidebar + the channel view.
  const talkChannel = /^\/chat\/c\/([^/]+)$/.exec(pathname)?.[1] ?? null;
  // The URL decides which pane is focused (a conversation replaces the focused one; split view is an
  // explicit "open side by side"). `/chat` itself is the company overview (or the new-chat screen).
  // biome-ignore lint/correctness/useExhaustiveDependencies: sync on route change only
  useEffect(() => {
    if (!routePane || routePane === 'chat') return;
    ws.show(routePane);
  }, [routePane]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: run on path change only
  useEffect(() => setDrawer(false), [pathname]);

  const prevPending = useRef<number | null>(null);
  useEffect(() => {
    const n = status.data?.pendingApprovals;
    if (n == null) return;
    if (prevPending.current != null && n > prevPending.current) {
      toast.warning(`${n - prevPending.current} approval(s) waiting`, {
        action: { label: 'Open', onClick: () => void navigate({ to: '/approvals' }) },
      });
    }
    prevPending.current = n;
  }, [status.data?.pendingApprovals, navigate]);

  const openLauncher = (initial = '') => {
    setPaletteInit(initial);
    setPalette(true);
  };
  const toggleSidebar = () => {
    if (isDesktop()) {
      setSidebarOpen((o) => {
        safeStorage.set('af:sidebar', !o);
        return !o;
      });
    } else setDrawer(true);
  };
  const togglePanel = () => {
    const next = helpChatOpen || !panelOpen;
    setHelpChatOpen(false);
    if (isWide()) safeStorage.set('af:panel', next);
    setPanelPref(next);
  };
  const hotkeysRef = useRef({ toggleSidebar, togglePanel });
  hotkeysRef.current = { toggleSidebar, togglePanel };
  // biome-ignore lint/correctness/useExhaustiveDependencies: handlers read latest state via refs/setters
  const hotkeys = useMemo(
    () => ({
      onPalette: () =>
        setPalette((p) => {
          if (!p) setPaletteInit('');
          return !p;
        }),
      onSlash: () => openLauncher(),
      onAsk: () => openLauncher('@'),
      onGo: (key: string) => {
        const item = NAV.find((n) => n.hotkey === key);
        if (item) return void navigate({ to: item.to });
        const extra = EXTRA_GO.find((g) => g.hotkey === key);
        // biome-ignore lint/suspicious/noExplicitAny: static extra destinations
        if (extra) void navigate({ to: extra.to as any, search: extra.search as any });
      },
      onHelp: () => setHelp(true),
      onSidebar: () => hotkeysRef.current.toggleSidebar(),
      onPanel: () => hotkeysRef.current.togglePanel(),
    }),
    [navigate],
  );
  useHotkeys(hotkeys);
  // biome-ignore lint/correctness/useExhaustiveDependencies: subscribe once
  useEffect(() => {
    const open = (e: Event) => openLauncher(((e as CustomEvent<unknown>).detail as string | undefined) ?? '');
    window.addEventListener(OPEN_PALETTE_EVENT, open);
    return () => window.removeEventListener(OPEN_PALETTE_EVENT, open);
  }, []);

  if (me.isPending) {
    return (
      <div className="flex h-dvh items-center justify-center bg-page text-muted">
        <Spinner /> <span className="ml-2">{t('common.loading')}</span>
      </div>
    );
  }
  if (!me.data) return <Empty icon="✗">Could not load your profile. Try reloading.</Empty>;

  const counts = { approvals: status.data?.pendingApprovals, notifications: unread.data };
  const inWorkspace = !!routePane;
  const chatRoute = routePane === 'chat' || routePane === 'master' || !!routePane?.startsWith('chat:');
  const focusedAgent = ws.state.focused ? (ws.state.meta[ws.state.focused]?.agentId ?? null) : null;
  // Phone: chats are two screens (list ↔ conversation), the conversation owning the whole height.
  const phoneChatList = phone && routePane === 'chat' && !search.new && !search.agent;
  const phoneChatNew = phone && routePane === 'chat' && !phoneChatList;
  const phoneConversation = phone && ((chatRoute && routePane !== 'chat') || !!talkChannel);
  const expanded = !!ws.state.expanded && inWorkspace;
  // The side panel also follows pages that are not panes (approvals, tasks) with their own summary.
  const panelPage = inWorkspace || contextKindFor(pathname) !== null;
  const showPanel = panelPage && panelOpen && !expanded;

  const sidebarProps = {
    counts,
    spendToday: status.data?.costTodayUsd,
    activeRuns: status.data?.activeRuns,
    onShortcuts: () => setHelp(true),
  };

  const announcement =
    me.data.announcement && me.data.announcement.id !== dismissedAnnouncement ? me.data.announcement : null;
  const banners = (me.data.impersonation ? 1 : 0) + (announcement ? 1 : 0);

  return (
    <RoleProvider role={role}>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:rounded-md focus:bg-cta focus:px-3 focus:py-1 focus:text-cta-fg"
      >
        {t('shell.skipToContent')}
      </a>
      {me.data.impersonation && (
        <SupportBanner name={me.data.user.name} until={me.data.impersonation.expiresAt} />
      )}
      {announcement && <PlatformNotice announcement={announcement} onDismiss={dismissAnnouncement} />}
      {/* Full-bleed app: no outer frame, no fake window chrome. Each banner above takes 2.25rem. */}
      <div
        data-testid="app-shell"
        className={cn('flex overflow-hidden bg-bg', !banners && 'h-dvh')}
        style={banners ? { height: `calc(100dvh - ${banners * 2.25}rem)` } : undefined}
      >
        {sidebarOpen && !expanded ? <Sidebar {...sidebarProps} className="hidden lg:flex" /> : null}
        {/* Phone / tablet drawer */}
        <div className={cn('fixed inset-0 z-40 lg:hidden', drawer ? 'block' : 'hidden')}>
          <button
            type="button"
            aria-label={t('common.close')}
            className="absolute inset-0 bg-[var(--overlay)]"
            onClick={() => setDrawer(false)}
          />
          <div
            className="relative z-10 h-full w-[280px] max-w-[85vw] shadow-pop"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            <Sidebar {...sidebarProps} onNavigate={() => setDrawer(false)} className="w-full" />
          </div>
        </div>
        <div
          className={cn(
            'flex min-w-0 flex-1 flex-col',
            phoneConversation && 'pb-[env(safe-area-inset-bottom)]',
          )}
        >
          {status.data?.agentsHalted ? (
            <AnnouncementBanner
              tone="danger"
              icon={<IconStop size={14} />}
              cta={{ label: t('shell.haltedCta'), to: '/runs' }}
            >
              {t('shell.halted')}
            </AnnouncementBanner>
          ) : null}
          {me.data.needs2fa ? (
            <AnnouncementBanner
              icon={<IconLock size={14} />}
              cta={{
                label: t('shell.twoFaSetup'),
                to: '/settings/$section',
                params: { section: 'security' },
              }}
            >
              {t('shell.twoFaRequired')}
            </AnnouncementBanner>
          ) : null}
          {priceMissing ? (
            <AnnouncementBanner
              icon={<IconAlert size={14} />}
              dismissKey="price-missing"
              cta={{
                label: t('shell.priceMissingCta'),
                to: '/settings/$section',
                params: { section: 'models' },
              }}
            >
              {t('shell.priceMissing')}
            </AnnouncementBanner>
          ) : null}
          <TopBar
            helpLauncher={hasOrg ? <HelpLauncher open={showHelpChat} onOpenChange={setHelpChatOpen} /> : null}
            me={me.data}
            halted={!!status.data?.agentsHalted}
            unread={unread.data}
            pendingApprovals={status.data?.pendingApprovals}
            onSidebar={toggleSidebar}
            onBack={phoneConversation || phoneChatNew ? () => void navigate({ to: '/chat' }) : undefined}
            onPanel={togglePanel}
            panelOpen={showPanel && !showHelpChat}
            panelAvailable={panelPage}
            onPalette={() => openLauncher()}
            onAsk={() => openLauncher('@')}
          />
          {/* From lg up the workspace is exactly as tall as the viewport (panes scroll inside), never as tall as its content. */}
          <div className="flex min-h-0 flex-1">
            <main
              id="main"
              className={cn(
                'min-w-0 flex-1 overflow-y-auto',
                talkChannel && 'overflow-hidden',
                showHelpChat && 'hidden lg:block',
              )}
            >
              {!hasOrg ? (
                <Empty icon="∅">
                  You are not a member of any organization yet. Ask an admin for an invitation.
                </Empty>
              ) : inWorkspace ? (
                <div
                  className={cn(
                    'flex min-h-full flex-col gap-3 p-2 sm:p-3 lg:h-full lg:flex-row',
                    chatRoute && 'max-lg:h-full',
                  )}
                >
                  {chatRoute ? (
                    <TalkSidebar
                      className={
                        routePane === 'chat' && !phoneChatNew
                          ? 'max-lg:min-h-0 max-lg:flex-1'
                          : 'hidden lg:flex'
                      }
                    />
                  ) : null}
                  <div
                    className={cn('flex min-h-0 min-w-0 flex-1 flex-col gap-3', phoneChatList && 'hidden')}
                  >
                    {routePane === 'chat' ? (
                      // Without agents the first step is the new-chat screen (provider gate, templates, AI designer).
                      search.new || search.agent || !hasAgents ? (
                        <NewChat />
                      ) : (
                        <CompanyOverview
                          onSelectAgent={(id) => {
                            setCompanyAgent((prev) => ({ id, selection: (prev?.selection ?? 0) + 1 }));
                            setPanelPref(true);
                            ws.expand(null);
                            setHelpChatOpen(false);
                          }}
                        />
                      )
                    ) : (
                      <Workspace routePane={routePane} chatsOnly={chatRoute} />
                    )}
                    <Outlet />
                  </div>
                </div>
              ) : talkChannel ? (
                <div className="flex h-full min-h-0 gap-3 p-2 sm:p-3">
                  <TalkSidebar className="hidden lg:flex" />
                  <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border bg-panel">
                    <ChannelView
                      channelId={decodeURIComponent(talkChannel)}
                      thread={search.thread}
                      onThreadChange={(thread) =>
                        void navigate({
                          to: '/chat/c/$channelId',
                          params: { channelId: decodeURIComponent(talkChannel) },
                          search: thread ? { thread } : {},
                          replace: true,
                        })
                      }
                    />
                  </section>
                  <Outlet />
                </div>
              ) : (
                <div className="w-full px-3 py-4 sm:px-6 lg:px-8">
                  <Outlet />
                </div>
              )}
            </main>
            {showHelpChat ? <HelpPanel onClose={closeHelpChat} /> : null}
            {showPanel && !showHelpChat ? (
              <>
                <div className="hidden w-[380px] shrink-0 border-l border-border min-[1800px]:w-[440px] 2xl:block">
                  <RightPanel
                    key={routePane === 'chat' ? companyAgent?.selection : undefined}
                    selectedAgentId={routePane === 'chat' ? companyAgent?.id : null}
                    focusedAgentId={focusedAgent}
                    onCollapse={togglePanel}
                    pathname={pathname}
                  />
                </div>
                <div className="fixed inset-0 z-40 2xl:hidden">
                  <button
                    type="button"
                    aria-label={t('common.close')}
                    className="absolute inset-0 bg-[var(--overlay)]"
                    onClick={togglePanel}
                  />
                  <div
                    className="absolute inset-y-0 right-0 w-full max-w-[440px] shadow-pop max-sm:inset-y-auto max-sm:bottom-0 max-sm:h-[85dvh] max-sm:max-w-none max-sm:overflow-hidden max-sm:rounded-t-2xl max-sm:border-t max-sm:border-border"
                    style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
                  >
                    <RightPanel
                      key={routePane === 'chat' ? companyAgent?.selection : undefined}
                      selectedAgentId={routePane === 'chat' ? companyAgent?.id : null}
                      focusedAgentId={focusedAgent}
                      onCollapse={togglePanel}
                      pathname={pathname}
                    />
                  </div>
                </div>
              </>
            ) : null}
          </div>
          {hasOrg ? (
            <BottomNav pendingApprovals={status.data?.pendingApprovals} onMore={() => setDrawer(true)} />
          ) : null}
        </div>
      </div>
      <CommandPalette
        open={palette}
        onOpenChange={setPalette}
        initialInput={paletteInit}
        onShortcuts={() => setHelp(true)}
      />
      <ShortcutsOverlay open={help} onOpenChange={setHelp} />
    </RoleProvider>
  );
}

/** Shown for the whole support session (platform admin signed in as the user): read-only, clearly marked. */
const DISMISSED_KEY = 'af:announcement-dismissed';
const readDismissed = () => {
  try {
    return globalThis.localStorage?.getItem(DISMISSED_KEY) ?? null;
  } catch {
    return null;
  }
};

/** Platform-wide notice from the admin app; dismissing hides this message (a new one shows again). */
function PlatformNotice({ announcement, onDismiss }: { announcement: Announcement; onDismiss: () => void }) {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      className={cn(
        'flex h-9 items-center justify-center gap-3 px-3 text-sm',
        announcement.tone === 'warn' ? 'bg-warn font-medium text-black' : 'bg-accent text-white',
      )}
    >
      <span className="min-w-0 truncate">{announcement.message}</span>
      <button type="button" className="shrink-0 underline" onClick={onDismiss}>
        {t('shell.dismiss')}
      </button>
    </div>
  );
}

function SupportBanner({ name, until }: { name: string; until: string }) {
  const { t, i18n } = useTranslation();
  return (
    <div
      role="status"
      className="flex h-9 items-center justify-center gap-3 bg-warn px-3 text-sm font-medium text-black"
    >
      {t('support.banner', { name, time: new Date(until).toLocaleTimeString(i18n.language) })}
      <button
        type="button"
        className="underline"
        onClick={async () => {
          await authClient.signOut();
          window.location.assign('/login');
        }}
      >
        {t('support.signOut')}
      </button>
    </div>
  );
}

export function AppShell() {
  const me = useMe();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { t } = useTranslation();
  if (me.isPending) return <Spinner />;
  const securityOnly = (
    <div className="mx-auto max-w-3xl p-6">
      <a href="/billing" className="text-accent">
        ← {t('billing.title')}
      </a>
      <SecuritySection />
    </div>
  );
  // Hosted accounts (billing on) secure the owner first: no workspace and no plan choice before a second factor.
  if (me.data?.needs2fa && me.data.billing?.enabled && !me.data.impersonation)
    return pathname === '/settings/security' ? securityOnly : <SecureAccountGate />;
  if (me.data?.billing?.enabled && !me.data.billing.hasAccess) {
    if (pathname === '/settings/security') return securityOnly;
    return <BillingPage />;
  }
  return (
    <WorkspaceProvider>
      <Frame />
    </WorkspaceProvider>
  );
}
